import React, { useRef } from "react";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment from 'moment';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import ClassTypeData from "../../../Config/classType.json";
import UserTypeData from "../../../Config/UserType.json";
import configCountries from "../../../Config/countries.json";

const AdvancedSearchForm = ({
    show,
    filters,
    setFilters,
    onSearch,
    onClear,
    promiseSchoolOptions,
    t
}) => {
    const datePickerRef = useRef(null);

    // Prepare lists
    const userTypes = UserTypeData.UserType;
    const classTypes = ClassTypeData.ClassType;
    const countriesList = configCountries.countries
        .sort((a, b) => (a.label > b.label ? 1 : -1))
        .map((item) => ({
            value: item.value,
            name: item.label,
            label: (
                <div className="d-flex align-items-center gap-2">
                    <LazyLoadImage src={process.env.PUBLIC_URL + item.icon} alt={item.label} style={{ width: '18px', height: '13px' }} />
                    <span className="mr-2" style={{ fontSize: '0.9rem' }}>{item.label}</span>
                </div>
            ),
        }));

    // Date range configurations
    let ranges = {};
    ranges[t("range.Today")] = [moment().toDate(), moment().toDate()];
    ranges[t("range.Tomorrow")] = [moment().subtract(-1, 'days'), moment().subtract(-1, 'days')];
    ranges[t("range.Next7Days")] = [moment().toDate(), moment().subtract(-6, 'days'), moment()];
    ranges[t("range.Next30Days")] = [moment().toDate(), moment().subtract(-29, 'days'), moment()];
    ranges[t("range.ThisMonth")] = [moment().startOf('month'), moment().endOf('month')];
    ranges[t("range.NextMonth")] = [moment().subtract(-1, 'month').startOf('month'), moment().subtract(-1, 'month').endOf('month')];

    const handleApplyDate = (event, picker) => {
        picker.element.val(picker.startDate.format('DD/MM/YYYY') + " - " + picker.endDate.format('DD/MM/YYYY'));
        setFilters(prev => ({
            ...prev,
            startActiveDate: picker.startDate.format('DD/MM/YYYY'),
            endActiveDate: picker.endDate.format('DD/MM/YYYY')
        }));
    };

    const handleCancelDate = (event, picker) => {
        picker.element.val('');
        setFilters(prev => ({
            ...prev,
            startActiveDate: "",
            endActiveDate: ""
        }));
    };

    const handleSelectChange = (field, option) => {
        setFilters(prev => ({
            ...prev,
            [field]: option ? option.value : ""
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const selectStyle = {
        theme: theme => ({
            ...theme,
            borderRadius: 8,
            colors: {
                ...theme.colors,
                primary25: '#92278f',
                primary: '#92278f',
            },
        }),
        styles: {
            control: (provided) => ({
                ...provided,
                borderRadius: '8px',
                borderColor: '#cbd5e1',
                boxShadow: 'none',
                height: '40px',
                minHeight: '40px',
                fontSize: '0.9rem',
                '&:hover': {
                    borderColor: '#92278f'
                }
            }),
            menu: (provided) => ({
                ...provided,
                zIndex: 1000
            })
        }
    };

    if (!show) return null;

    return (
        <div className="shadow-sm border-0 mb-4 bg-white text-right w-100" style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <div className="p-4" style={{ backgroundColor: '#ffffff' }}>
                <div className="row">
                    
                    {/* Group 1: Personal Details */}
                    <div className="col-lg-4 col-md-12 mb-3">
                        <div className="filter-card">
                            <h6 className="filter-section-title">
                                <i className="fal fa-user-edit ml-2 text-primary" style={{ color: '#92278f' }}></i>
                                {t("Pages.UsersControl.advancedSearch.personalSection")}
                            </h6>
                            
                            <div className="form-group mb-3">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-user ml-1 text-primary"></i>
                                    {t("Pages.UsersControl.advancedSearch.name")}
                                </label>
                                <input
                                    value={filters.Name}
                                    onChange={handleInputChange}
                                    type="text"
                                    className="form-control adv-search-input"
                                    name="Name"
                                    placeholder={t("Pages.UsersControl.advancedSearch.name")}
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-at ml-1 text-primary"></i>
                                    {t("Pages.UsersControl.advancedSearch.username")}
                                </label>
                                <input
                                    value={filters.Username}
                                    onChange={handleInputChange}
                                    type="text"
                                    className="form-control adv-search-input"
                                    name="Username"
                                    placeholder={t("Pages.UsersControl.advancedSearch.username")}
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-envelope ml-1 text-primary"></i>
                                    {t("Pages.UsersControl.advancedSearch.email")}
                                </label>
                                <input
                                    value={filters.Email}
                                    onChange={handleInputChange}
                                    type="text"
                                    className="form-control adv-search-input"
                                    name="Email"
                                    placeholder={t("Pages.UsersControl.advancedSearch.email")}
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-phone-alt ml-1 text-primary"></i>
                                    {t("Pages.UsersControl.advancedSearch.phone")}
                                </label>
                                <input
                                    value={filters.Phone}
                                    onChange={handleInputChange}
                                    type="text"
                                    className="form-control adv-search-input"
                                    name="Phone"
                                    placeholder={t("Pages.UsersControl.advancedSearch.phone")}
                                />
                            </div>

                            <div className="form-group mb-0">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-list-ol ml-1 text-primary"></i>
                                    {t("Pages.UsersControl.advancedSearch.limit")}
                                </label>
                                <input
                                    value={filters.Limit}
                                    onChange={(e) => setFilters(prev => ({ ...prev, Limit: parseInt(e.target.value) || 0 }))}
                                    type="number"
                                    step={1}
                                    className="form-control adv-search-input"
                                    name="Limit"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Group 2: Classification Details */}
                    <div className="col-lg-4 col-md-12 mb-3">
                        <div className="filter-card">
                            <h6 className="filter-section-title">
                                <i className="fal fa-graduation-cap ml-2 text-success"></i>
                                {t("Pages.UsersControl.advancedSearch.classificationSection")}
                            </h6>

                            <div className="form-group mb-3">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-user-tag ml-1 text-success"></i>
                                    {t("Pages.UsersControl.UserType.title")}
                                </label>
                                <select
                                    value={filters.UserTypeId}
                                    onChange={(e) => setFilters(prev => ({ ...prev, UserTypeId: parseInt(e.target.value) }))}
                                    className="form-control adv-select-field"
                                    name="UserTypeId"
                                >
                                    <option value={0}>{t("Pages.UsersControl.advancedSearch.UserType")}</option>
                                    {userTypes.map(item => (
                                        <option key={item.Id} value={item.Id}>{item.Name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group mb-3">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-school ml-1 text-success"></i>
                                    {t("Pages.UsersControl.advancedSearch.School")}
                                </label>
                                <AsyncSelect
                                    cacheOptions
                                    isClearable={true}
                                    placeholder={t('Pages.UsersControl.advancedSearch.School')}
                                    isRtl={true}
                                    isSearchable={true}
                                    defaultOptions
                                    loadOptions={promiseSchoolOptions}
                                    onChange={(opt) => handleSelectChange("SchoolId", opt)}
                                    value={filters.SchoolId ? { value: filters.SchoolId, label: filters.SchoolName || "" } : null}
                                    {...selectStyle}
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-graduation-cap ml-1 text-success"></i>
                                    {t("Pages.UsersControl.advancedSearch.ClassType.title")}
                                </label>
                                <select
                                    value={filters.ClassTypeId}
                                    onChange={(e) => setFilters(prev => ({ ...prev, ClassTypeId: parseInt(e.target.value) }))}
                                    className="form-control adv-select-field"
                                    name="ClassTypeId"
                                >
                                    <option value={0}>{t("Pages.UsersControl.advancedSearch.ClassType.Placeholder")}</option>
                                    {classTypes.map(item => (
                                        <option key={item.Id} value={item.Id}>{item.Name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group mb-3">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-globe-asia ml-1 text-success"></i>
                                    {t("Pages.Register.Country.Placeholder")}
                                </label>
                                <Select
                                    value={countriesList.find(c => c.value === filters.Country) || null}
                                    onChange={(opt) => handleSelectChange("Country", opt)}
                                    placeholder={t('Pages.Register.Country.Placeholder')}
                                    isClearable={true}
                                    isRtl={true}
                                    isSearchable={true}
                                    options={countriesList}
                                    {...selectStyle}
                                />
                            </div>

                            <div className="form-group mb-0">
                                <label className="text-muted small font-weight-bold mb-1">
                                    <i className="fad fa-venus-mars ml-1 text-success"></i>
                                    {t("Pages.UsersControl.advancedSearch.Sex")}
                                </label>
                                <select
                                    value={filters.Sex}
                                    onChange={handleInputChange}
                                    className="form-control adv-select-field"
                                    name="Sex"
                                >
                                    <option value="">{t("Pages.UsersControl.advancedSearch.Sex")}</option>
                                    <option value="male">{t("Pages.Register.Sex.male")}</option>
                                    <option value="female">{t("Pages.Register.Sex.female")}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Group 3: Status Details */}
                    <div className="col-lg-4 col-md-12 mb-3">
                        <div className="filter-card d-flex flex-column justify-content-between">
                            <div>
                                <h6 className="filter-section-title">
                                    <i className="fal fa-shield-alt ml-2 text-warning"></i>
                                    {t("Pages.UsersControl.advancedSearch.statusSection")}
                                </h6>

                                <div className="form-group mb-3">
                                    <label className="text-muted small font-weight-bold mb-1">
                                        <i className="fad fa-eye ml-1 text-warning"></i>
                                        {t("Pages.UsersControl.advancedSearch.IsActive")}
                                    </label>
                                    <select
                                        value={filters.IsActive === null ? "" : filters.IsActive.toString()}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            IsActive: e.target.value === "" ? null : e.target.value === "true"
                                        }))}
                                        className="form-control adv-select-field"
                                    >
                                        <option value="">{t("Pages.UsersControl.advancedSearch.IsActive")}</option>
                                        <option value="true">{t("Pages.UsersControl.Active")}</option>
                                        <option value="false">{t("Pages.UsersControl.NotActive")}</option>
                                    </select>
                                </div>

                                <div className="form-group mb-3">
                                    <label className="text-muted small font-weight-bold mb-1">
                                        <i className="fad fa-lock-alt ml-1 text-warning"></i>
                                        {t("Pages.UsersControl.advancedSearch.IsLocked")}
                                    </label>
                                    <select
                                        value={filters.IsLocked === null ? "" : filters.IsLocked.toString()}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            IsLocked: e.target.value === "" ? null : e.target.value === "true"
                                        }))}
                                        className="form-control adv-select-field"
                                    >
                                        <option value="">{t("Pages.UsersControl.advancedSearch.IsLocked")}</option>
                                        <option value="false">{t("Pages.UsersControl.NotLocked")}</option>
                                        <option value="true">{t("Pages.UsersControl.Locked")}</option>
                                    </select>
                                </div>

                                <div className="form-group mb-3">
                                    <label className="text-muted small font-weight-bold mb-1">
                                        <i className="fad fa-trash-alt ml-1 text-warning"></i>
                                        {t("Pages.UsersControl.advancedSearch.IsDeleted")}
                                    </label>
                                    <select
                                        value={filters.IsDeleted ? "true" : "false"}
                                        onChange={(e) => setFilters(prev => ({ ...prev, IsDeleted: e.target.value === "true" }))}
                                        className="form-control adv-select-field"
                                        name="IsDeleted"
                                    >
                                        <option value="false">{t("Pages.UsersControl.IsNotDeleted")}</option>
                                        <option value="true">{t("Pages.UsersControl.IsDeleted")}</option>
                                    </select>
                                </div>

                                <div className="form-group mb-3">
                                    <label className="text-muted small font-weight-bold mb-1">
                                        <i className="fad fa-calendar-alt ml-1 text-warning"></i>
                                        {t("Pages.UsersControl.advancedSearch.activeDate")}
                                    </label>
                                    <div className="position-relative">
                                        <DateRangePicker
                                            ref={datePickerRef}
                                            initialSettings={{
                                                opens: 'center',
                                                autoUpdateInput: false,
                                                buttonClasses: 'btn',
                                                cancelClass: "btn-danger",
                                                applyButtonClasses: "btn-primary",
                                                showDropdowns: true,
                                                ranges: ranges,
                                                minYear: 2020,
                                                locale: {
                                                    format: 'DD/MM/YYYY',
                                                    cancelLabel: t('Close'),
                                                    firstDay: 6,
                                                    applyLabel: t('Apply'),
                                                    customRangeLabel: t('range.customRange'),
                                                    monthNames: [
                                                        t("months.January"), t("months.February"), t("months.March"), t("months.April"),
                                                        t("months.May"), t("months.June"), t("months.July"), t("months.August"),
                                                        t("months.September"), t("months.October"), t("months.November"), t("months.December")
                                                    ],
                                                    daysOfWeek: [
                                                        t("daysOfWeek.Sunday"), t("daysOfWeek.Monday"), t("daysOfWeek.Tuesday"),
                                                        t("daysOfWeek.Wednesday"), t("daysOfWeek.Thursday"), t("daysOfWeek.Friday"),
                                                        t("daysOfWeek.Saturday")
                                                    ],
                                                },
                                            }}
                                            onApply={handleApplyDate}
                                            onCancel={handleCancelDate}
                                        >
                                            <input
                                                value={filters.startActiveDate ? `${filters.startActiveDate} - ${filters.endActiveDate}` : ""}
                                                name="Date"
                                                autoComplete="off"
                                                type="text"
                                                placeholder={t('Pages.UsersControl.advancedSearch.activeDate')}
                                                className="form-control adv-search-input pl-4 text-right"
                                                readOnly
                                            />
                                        </DateRangePicker>
                                        {filters.startActiveDate && (
                                            <div
                                                className="btn btn-sm position-absolute d-flex align-items-center justify-content-center"
                                                onClick={() => setFilters(prev => ({ ...prev, startActiveDate: "", endActiveDate: "" }))}
                                                style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}
                                            >
                                                <i className="fas fa-times text-danger"></i>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Form action buttons inside section 3 */}
                            <div className="d-flex gap-3 mt-4">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary w-50 adv-clear-btn"
                                    onClick={onClear}
                                    style={{ height: '42px', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem' }}
                                >
                                    <i className="fal fa-undo ml-2"></i>
                                    {t("Pages.UsersControl.clearFilters")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary w-50 d-flex align-items-center justify-content-center adv-submit-btn"
                                    onClick={onSearch}
                                    style={{ height: '42px', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem' }}
                                >
                                    <span>
                                        <i className="fas fa-search ml-2"></i>
                                        {t('Pages.UsersControl.advancedSearch.send')}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default AdvancedSearchForm;
